export const EASE = [0.2, 0.8, 0.2, 1] as const

export const DURATION = { fast: 0.12, mid: 0.16, slow: 0.24 } as const

/**
 * When each piece of the summary arrives, in seconds from mount. Straight from
 * the source design's timing map — the screen reads top to bottom in the order
 * somebody reads it, and finishes a little under five seconds.
 */
export const AT = {
  ring: 0.1,
  ringDraw: 0.25,
  ringBadge: 0.95,
  title: 0.15,
  firstCheck: 0.45,
  checkGap: 0.12,
  checkDraw: 0.06,
  progress: 1.15,
  firstCard: 1.45,
  cardGap: 0.12,
  firstCardLink: 1.85,
  cardLinkGap: 0.06,
  cardBadge: 2.1,
  cardSteps: 2.35,
  action: 2.5,

  panel: 0.95,
  panelHeader: 1.2,
  panelMembers: 1.3,
  firstPanelStep: 1.9,
  panelStepGap: 0.1,
  memberRoll: 2.6,
  memberPill: 2.75,
  firstPanelLog: 2.75,
  panelLogGap: 0.4,
  panelPending: 4.75,
  emptyState: 4.6,
  emptyAction: 4.9
} as const

export const MAX_STAGGERED_ROWS = 6

const capped = (index: number): number => Math.min(index, MAX_STAGGERED_ROWS)

const timing = (animate: boolean, duration: number, delay: number) =>
  animate ? { duration, delay, ease: EASE } : { duration: 0 }

export const fadeIn = (animate: boolean, delay = 0) => ({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: timing(animate, 0.4, delay)
})

export const riseIn = (animate: boolean, delay = 0, duration = 0.5) => ({
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  transition: timing(animate, duration, delay)
})

export const slideRight = (animate: boolean, delay = 0) => ({
  initial: { opacity: 0, x: -8 },
  animate: { opacity: 1, x: 0 },
  transition: timing(animate, 0.4, delay)
})

export const popIn = (animate: boolean, delay = 0) => ({
  initial: { opacity: 0, scale: 0.92 },
  animate: { opacity: 1, scale: 1 },
  transition: timing(animate, 0.4, delay)
})

export const panelIn = (animate: boolean, delay = 0) => ({
  initial: { opacity: 0, y: 16, scale: 0.99 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: timing(animate, 0.7, delay)
})

export const rowIn = (animate: boolean, delay = 0) => ({
  initial: { opacity: 0, height: 0, y: -6 },
  animate: { opacity: 1, height: 'auto', y: 0 },
  transition: timing(animate, 0.5, delay)
})

/** Crossfade for a value that changes under the reader — a phase, a status. */
export const swap = (animate: boolean) => ({
  initial: { opacity: 0, y: 3 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -3 },
  transition: animate ? { duration: DURATION.mid, ease: EASE } : { duration: 0 }
})

export const SHEEN_DURATION = 1.4

/** One pass every five seconds. */
export const SHEEN_CYCLE = 5

/**
 * The band is a quarter of the button wide, so it has to travel past 4x its own
 * width to leave the button completely. `repeatDelay` parks an animation on its
 * final value, and at the design's 320% that final value still overlaps the
 * right edge — which read as a white smear stuck on the button between passes.
 */
export const SHEEN_TRAVEL = '420%'

export const sheenSweep = (animate: boolean) => ({
  initial: { x: '-150%' },
  animate: { x: SHEEN_TRAVEL },
  transition: animate
    ? {
        duration: SHEEN_DURATION,
        delay: 0.15,
        ease: EASE,
        repeat: Infinity,
        repeatDelay: SHEEN_CYCLE - SHEEN_DURATION
      }
    : { duration: 0 }
})

export const stepDelay = (first: number, gap: number, index: number): number =>
  first + capped(index) * gap

export const RING_SIZE = 64

export const RING_STROKE = 3

export const RING_AVATAR_SIZE = 44

export const COUNTER_SLOT_HEIGHT = 24

export const ACTIVITY_PANEL_MIN_WIDTH = 380

/**
 * The panel's own width, from the source design. Capped rather than fluid: the
 * slab behind it got wider, and a card that grows with it stops reading as an
 * object standing on a picture and starts reading as the picture's border.
 */
export const ACTIVITY_PANEL_WIDTH = 708

export const GATED_OPACITY = 0.45
